import cx from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';
import { Box, paletteColors, Stack, Tooltip, Typography } from '@evaneos/b2b-ui';
import DoneAll from '@evaneos/b2b-ui/icons/DoneAll';

import { useLongPressMessage } from '../hooks/useLongPressMessage';
import {
    DiscussionIsResponseRequiredState,
    Message as MessageType,
} from '../../../../resources/discussion/types';
import { DossierId } from '../../../../resources/dossier/types';
import { useAuth } from '../../../../shared/providers/AuthProvider';
import { getAgentDetailsFromEtsToken } from '../../../../resources/login/operations';
import { useIsTouchDevice } from '../../../../shared/hooks/useIsTouchDevice';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { messageHtmlConverter } from '../utils/messageHtmlConverter';
import { useActionButtonsCouldBeInside } from '../hooks/useActionButtonsCouldBeInside';
import {
    useIsLastMessageAndFromAgent,
    useIsLastMessageAndFromTraveller,
} from '../../../../resources/discussion/queries';
import { useGetDossierData } from '../../../../resources/dossier/queries';
import { useIsAT1Message } from '../hooks/AB_useIsAT1Message';
import {
    hasVotedForDossier,
    saveFeedback,
} from '../../../../shared/services/AB_at1FeedbackStorage';
import { generateAT1TraceId, sendAT1Feedback } from '../../../../shared/services/AB_langfuse';

import { EditMessageButton } from './EditMessageButton';
import s from './Discussion.css';
import { MessageAttachment } from './MessageAttachment';
import { AT1FeedbackBanner } from './AB_AT1FeedbackBanner';
import { AT1FeedbackModal } from './AB_AT1FeedbackModal';
import { ResponseRequiredIndicator } from './ResponseRequiredIndicator';

const MIN_MESSAGE_WIDTH_FOR_INSIDE_BUTTON = 230;
const EDIT_MESSAGE_CLASS = 'editMessageButton';

type Props = {
    message: MessageType;
    onEdit: () => void;
    isBlurred: boolean;
    isEditing: boolean;
    isResponseRequiredState: DiscussionIsResponseRequiredState;
};

export const Message = ({
    message,
    onEdit,
    isBlurred,
    isEditing,
    isResponseRequiredState,
}: Props): JSX.Element | null => {
    const { formatMessage, formatTime } = useIntl();
    const location = useLocation();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const isTouchDevice = useIsTouchDevice();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const isLastMessageFromAgent = useIsLastMessageAndFromAgent(dossierId, message.id);
    const isLastMessageFromTraveller = useIsLastMessageAndFromTraveller(dossierId, message.id);
    const { userId: currentAgentId } = useAuth();

    const [isMessageHovered, setIsMessageHovered] = useState(false);

    const {
        isFromAgent,
        isFromTraveller,
        isEvaneosNotification,
        sendDate,
        updatedAt,
        authorName,
        authorId,
        body,
        htmlBody,
        attachments,
        id,
        isMessageRead,
        provenance,
    } = message;
    const isAuthor = authorId === currentAgentId?.toString();

    const messageRef = useRef<HTMLDivElement>(null);
    const [editionExpired, setEditionExpired] = useState(
        differenceInMinutes(new Date(), sendDate) > 10,
    );

    const isEditButtonInside = useActionButtonsCouldBeInside({
        messageRef,
        minWidthForInsideButton: MIN_MESSAGE_WIDTH_FOR_INSIDE_BUTTON,
        enabled: isFromAgent,
    });

    useEffect(() => {
        if (!isLastMessageFromAgent || !isAuthor) {
            return;
        }

        const checkEvery30Secondes = setInterval(() => {
            setEditionExpired(differenceInMinutes(new Date(), sendDate) > 10);
        }, 30 * 1000);

        return () => clearInterval(checkEvery30Secondes);
    }, [sendDate, isLastMessageFromAgent, isAuthor]);

    const isEditable = useMemo(() => {
        return (
            isLastMessageFromAgent &&
            authorId.toString() === currentAgentId?.toString() &&
            !editionExpired &&
            !isBlurred &&
            !isEditing
        );
    }, [isLastMessageFromAgent, authorId, currentAgentId, editionExpired, isBlurred, isEditing]);

    const { isLongPressed, longPressEvents, setIsLongPressed } = useLongPressMessage({
        delay: 300,
        excludeTargets: [messageRef, EDIT_MESSAGE_CLASS],
        enabled: isEditable && isTouchDevice,
    });

    const isEdited = differenceInSeconds(updatedAt, sendDate) > 0;
    const showEditButtonOutside = isEditable && !isEditButtonInside;
    const showEditButtonInside = isEditable && isEditButtonInside;

    const messageBackgroundColor = useMemo(() => {
        if (isEvaneosNotification) {
            return paletteColors.blue[100];
        }

        if (isFromAgent) {
            return isLongPressed ? paletteColors.green[200] : paletteColors.green[100];
        }

        return paletteColors.neutral[100];
    }, [isEvaneosNotification, isFromAgent, isLongPressed]);

    /* vvv Maturity AB test ab_at1_feedback vvv PR #2501 */
    const isAT1Message = useIsAT1Message(message);
    const alreadyVoted = hasVotedForDossier(dossierId);
    const [hasClickedVote, setHasClickedVote] = useState(false);
    const [feedbackValue, setFeedbackValue] = useState<1 | 0 | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const { dossier } = useGetDossierData(dossierId) || {};
    const tripProjectId = dossier?.project?.maturityExperiments?.tripProjectId;
    const traceId = tripProjectId ? generateAT1TraceId(tripProjectId) : undefined;

    const handleVote = (value: 1 | 0) => {
        setFeedbackValue(value);
        setHasClickedVote(true); // Masquer la bannière immédiatement
        setModalOpen(true);
    };

    const handleModalClose = async (comment?: string) => {
        setModalOpen(false);

        if (feedbackValue === null || !traceId) return;

        try {
            const agentDetails = getAgentDetailsFromEtsToken();

            // Envoyer à Langfuse
            await sendAT1Feedback(traceId, feedbackValue, comment, {
                dossier_id: dossierId,
                agent_id: agentDetails?.agentId,
                agency_id: agentDetails?.agencyId,
            });

            // Sauvegarder dans localStorage
            saveFeedback(dossierId, {
                messageId: message.id,
                voted: true,
                feedbackValue,
            });
        } catch (error) {
            // Log uniquement, pas de UI error
            console.error('[AT1 Feedback] Failed to submit feedback:', error);
        }
    };
    /* ^^^ Maturity AB test ab_at1_feedback ^^^ PR #2501 */

    const onClickEditButton = () => {
        setIsLongPressed(false);
        onEdit();
        trackEvent((ampli, defaultProperties) =>
            ampli.messageEditClicked({ ...defaultProperties, ...trackedTripProperties }),
        );
    };

    return (
        <Stack
            id={`message-${id}`}
            flex={1}
            width="100%"
            className={cx({
                [s.travellerMessage]: isFromTraveller,
                [s.agentMessage]: isFromAgent,
                [s.highlight]: location.hash === `#message-${id}`,
            })}
            data-testid={`message-id-${id}`}
            sx={{
                ...(!isTouchDevice &&
                    !isEditing && {
                        [`&:hover .${EDIT_MESSAGE_CLASS}`]: { opacity: 1 },
                    }),
                filter: isBlurred ? 'blur(1.5px)' : 'none',
                opacity: isBlurred ? 0.6 : 1,
                pointerEvents: isBlurred ? 'none' : 'auto',
                transition: 'filter 0.4s ease-in-out, opacity 0.4s ease-in-out',
                willChange: 'filter, opacity',
                contain: 'paint',
            }}
            flexShrink={showEditButtonOutside ? 0 : 1}
            onMouseEnter={() => setIsMessageHovered(true)}
            onMouseLeave={() => setIsMessageHovered(false)}
        >
            <Stack flexDirection="column" alignItems={isFromAgent ? 'flex-end' : 'flex-start'}>
                <Box display="flex" alignItems="center">
                    {showEditButtonOutside && (
                        <EditMessageButton
                            className={EDIT_MESSAGE_CLASS}
                            onClick={onClickEditButton}
                            sx={{
                                position: 'sticky',
                                left: '16px',
                                opacity: isLongPressed ? 1 : 0,
                                ...(isTouchDevice && {
                                    visibility: isLongPressed ? 'visible' : 'hidden',
                                }),
                            }}
                        />
                    )}
                    <Box
                        ref={messageRef}
                        bgcolor={messageBackgroundColor}
                        sx={{
                            position: 'relative',
                            maxWidth: '720px',
                            borderRadius: '16px',
                            whiteSpace: provenance === 'mailgun' ? 'pre-line' : 'normal',
                            wordBreak: 'break-word',
                            width: 'fit-content',
                            transition: 'background-color 0.2s ease-in-out',
                        }}
                        className={s.message}
                    >
                        <Box sx={{ p: 2 }} {...longPressEvents}>
                            <Typography
                                variant="body2"
                                sx={{
                                    '& *': {
                                        fontSize: '1.4rem',
                                        lineHeight: '2rem',
                                        fontFamily: 'Open Sauce One, sans-serif',
                                    },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: messageHtmlConverter(body, htmlBody, provenance),
                                }}
                                data-testid="message-body"
                            />
                            <div className={s.attachments}>
                                {attachments.map((attachment) => (
                                    <MessageAttachment key={attachment.id} {...attachment} />
                                ))}
                            </div>
                        </Box>
                        {showEditButtonInside && (
                            <EditMessageButton
                                onClick={onClickEditButton}
                                className={EDIT_MESSAGE_CLASS}
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    opacity: isLongPressed ? 1 : 0,
                                    ...(isTouchDevice && {
                                        visibility: isLongPressed ? 'visible' : 'hidden',
                                    }),
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Stack direction="row" marginY={0.5} gap={0.5}>
                    <Typography
                        variant="caption"
                        color={isEvaneosNotification ? 'text.information' : 'text.secondary'}
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                    >
                        {isFromTraveller &&
                        (isLastMessageFromTraveller ? !isMessageHovered : true) ? (
                            <>
                                {authorName}
                                {' • '}
                            </>
                        ) : null}
                        {isEvaneosNotification &&
                            formatMessage({ id: 'discussion.schedule_messages.information' }) +
                                ' • '}
                        {(isLastMessageFromTraveller ? !isMessageHovered : true)
                            ? formatTime(sendDate)
                            : null}
                        {isEdited &&
                            ' (' +
                                formatMessage(
                                    { id: 'discussion.edit_message.updated_at' },
                                    {
                                        time: formatTime(updatedAt, {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }),
                                    },
                                ) +
                                ') '}
                        {isFromAgent ? (
                            <>
                                {' • '}
                                {isAuthor ? formatMessage({ id: 'global.you' }) : authorName}
                            </>
                        ) : null}
                        {isLastMessageFromTraveller && (
                            <ResponseRequiredIndicator
                                dossierId={dossierId}
                                isResponseRequiredState={isResponseRequiredState}
                                lastTravellerMessageId={
                                    typeof message.id === 'number' ? message.id : null
                                }
                                isMessageHovered={isMessageHovered}
                            />
                        )}
                    </Typography>
                    {isFromAgent && (
                        <Tooltip
                            title={formatMessage({
                                id: isMessageRead
                                    ? 'discussion.message_read'
                                    : 'discussion.message_sent',
                            })}
                        >
                            <DoneAll
                                htmlColor={isMessageRead ? 'info' : paletteColors.neutral[700]}
                                fontSize="small"
                            />
                        </Tooltip>
                    )}
                </Stack>

                {/* vvv Maturity AB test ab_at1_feedback vvv PR#2501 */}
                {isAT1Message && !alreadyVoted && !hasClickedVote && tripProjectId && (
                    <AT1FeedbackBanner onVote={handleVote} />
                )}

                <AT1FeedbackModal
                    open={modalOpen}
                    feedbackValue={feedbackValue}
                    onClose={(comment) => {
                        void handleModalClose(comment);
                    }}
                />
                {/* ^^^ Maturity AB test ab_at1_feedback ^^^ PR#2501 */}
            </Stack>
        </Stack>
    );
};
